/**
 * iOS keyboard handling for modals.
 *
 * iOS Safari does NOT shrink the layout viewport when the on-screen keyboard
 * opens (the keyboard is an overlay), so a modal sized in `dvh`/`vh` stays
 * full-height and its lower part — inputs and the footer CTA — ends up hidden
 * behind the keyboard. Android shrinks the layout viewport, so it's fine there.
 *
 * This mirrors the *visual* viewport (the area actually visible above the
 * keyboard) into CSS variables on <html> and toggles a `hopn-vv-active` class
 * while the keyboard is open. `styles.scss` then sizes the modal overlay + sheet
 * to that visible area so the focused input / footer stay above the keyboard.
 *
 * Gated to iOS so Android / desktop behaviour is left exactly as-is.
 */

import {isIOS} from './platform.js';

// How much viewport height must be lost before we treat the keyboard as "open".
// iOS keyboards are well over this; small toolbar changes stay under it.
const KEYBOARD_THRESHOLD = 150;

export function installIOSKeyboardViewport() {
    if (typeof document === 'undefined' || !isIOS()) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    if (root.dataset.iosVvInstalled === '1') return;
    root.dataset.iosVvInstalled = '1';

    const isTextField = (el) => !!el && (
        el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable === true
    );

    const sync = () => {
        // Only treat the viewport as keyboard-shrunk when a text field is focused.
        // Otherwise plain scrolling (which collapses/expands the iOS URL bar and
        // fires visualViewport events) would flip the class on/off mid-scroll,
        // re-sizing/offsetting the modal overlay — that caused the shaking and
        // briefly revealed the page behind at the top.
        const keyboardOpen =
            isTextField(document.activeElement) &&
            (window.innerHeight - vv.height) > KEYBOARD_THRESHOLD;
        if (keyboardOpen) {
            root.style.setProperty('--hopn-vvh', `${Math.round(vv.height)}px`);
            root.style.setProperty('--hopn-vv-top', `${Math.round(vv.offsetTop)}px`);
        }
        root.classList.toggle('hopn-vv-active', keyboardOpen);
    };

    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    // Focus changes gate the keyboard state, so re-evaluate on them too.
    document.addEventListener('focusin', sync);
    document.addEventListener('focusout', () => setTimeout(sync, 50));
    sync();
}

export default installIOSKeyboardViewport;
